import { IEnrichmentModel } from "@app/shared/models/enrichment.model";
import {
  IProdSpecCharValueUse,
  IProductSpecCharacteristicValue,
} from "@app/shared/models/product-offering.model";
import {
  SUPPORTED_LOCALES,
  SupportedLocale,
} from "@app/shared/utils/multilingual.util";
import { ActiveLanguage } from "@app/shared/services/language-config.store";

/** Display code shown in the hero language cards — the channel row's
 *  `shortCode` (e.g. "TH"/"EN"/"MM"/"VN"). */
export type LanguageDisplayCode = string;

export interface LanguageCoverage {
  /** Canonical locale code, e.g. "th-TH". */
  locale: SupportedLocale;
  /** Short code shown in the card header. */
  code: LanguageDisplayCode;
  /** Long label (currently same as code, kept for future i18n). */
  label: string;
  /** Number of multilingual slots filled for this locale. */
  filled: number;
  /** Total number of multilingual slots discovered across the package. */
  total: number;
  /** Convenience: 0–100. Zero when total = 0. */
  percent: number;
}

/** Type guard — a multilingual value object has at least one of the supported
 *  locale keys. Strings/numbers/arrays don't count. */
function isMultilingualBag(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const obj = value as Record<string, unknown>;
  return SUPPORTED_LOCALES.some((l) => l in obj);
}

function pickValue(v: IProductSpecCharacteristicValue | undefined): unknown {
  return v?.value;
}

function collectMultilingualBags(
  model: IEnrichmentModel | null,
): Record<string, unknown>[] {
  if (!model) return [];

  const bags: Record<string, unknown>[] = [];

  const addFromCharValueUses = (list: IProdSpecCharValueUse[] | undefined) => {
    if (!list) return;
    for (const use of list) {
      for (const v of use.productSpecCharacteristicValue ?? []) {
        const raw = pickValue(v);
        if (isMultilingualBag(raw)) bags.push(raw);
      }
    }
  };

  addFromCharValueUses(model.productOffering?.prodSpecCharValueUse);
  for (const pop of model.productOfferingPrice ?? []) {
    addFromCharValueUses(pop?.prodSpecCharValueUse);
  }

  return bags;
}

/** Compute per-language filled/total counts across every multilingual
 *  characteristic in the enrichment package. Pure — no I/O. `activeLangs` is
 *  the channel's API-driven set (drives card code/label + which locales count). */
export function computeLanguageCoverage(
  model: IEnrichmentModel | null,
  activeLangs: readonly ActiveLanguage[],
): LanguageCoverage[] {
  const codeFor = (locale: string): string =>
    activeLangs.find((a) => a.locale === locale)?.shortCode ?? locale;

  const zero = (): LanguageCoverage[] =>
    SUPPORTED_LOCALES.map((locale) => ({
      locale,
      code: codeFor(locale),
      label: codeFor(locale),
      filled: 0,
      total: 0,
      percent: 0,
    }));

  const bags = collectMultilingualBags(model);
  if (bags.length === 0) return zero();

  const counters: Record<string, { filled: number; total: number }> = {};
  for (const locale of SUPPORTED_LOCALES) {
    counters[locale] = { filled: 0, total: 0 };
  }

  for (const bag of bags) {
    for (const locale of SUPPORTED_LOCALES) {
      counters[locale].total += 1;
      const v = bag[locale];
      if (typeof v === "string" && v.trim().length > 0) {
        counters[locale].filled += 1;
      }
    }
  }

  return SUPPORTED_LOCALES.map((locale) => {
    const c = counters[locale];
    const percent = c.total > 0 ? Math.round((c.filled / c.total) * 100) : 0;
    return {
      locale,
      code: codeFor(locale),
      label: codeFor(locale),
      filled: c.filled,
      total: c.total,
      percent,
    };
  });
}

/** Overall readiness percentage derived from PublishReadiness section list.
 *  Falls back to 0 when no sections are available. */
export function computeReadinessPercent(
  sections: ReadonlyArray<{ status: string }> | undefined | null,
): { percent: number; complete: number; total: number } {
  if (!sections || sections.length === 0)
    return { percent: 0, complete: 0, total: 0 };
  const complete = sections.filter((s) => s.status === "complete").length;
  const percent = Math.round((complete / sections.length) * 100);
  return { percent, complete, total: sections.length };
}
